import React from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { cn } from '@/design-system/lib/utils';
import { Tag } from '@/design-system/tag';

import {
  OUTBOUND_TYPE_META,
  PO_TYPE_META,
  poSourceOf,
  poTypeOf,
} from '../status';
import type { OutboundOrder, PurchaseOrder, PurchaseOrderType } from '../types';
import { KV, linkCls, metaCls, nameCls } from './ui';
import { WAREHOUSE_PATHS } from '../paths';

/**
 * PO type (Regular / Transfer / Return) and outbound type (Regular /
 * Transfer) — the tags, the "where from" cell and the list filter, shared by
 * PO, inbound request, goods receipt and outbound screens.
 */

export const PoTypeTag: React.FC<{ type: PurchaseOrderType | undefined }> = ({
  type,
}) => {
  const t = type ?? 'REGULAR';
  const meta = PO_TYPE_META[t] ?? PO_TYPE_META.REGULAR;
  return (
    <Tag
      size="xs"
      variant={t === 'REGULAR' ? 'gray' : 'stroke'}
      icon={<i className={cn(meta.icon, 'text-[11px]')} />}
      data-testid="po-type-tag"
    >
      {meta.label}
    </Tag>
  );
};

type PoSourceFields = Pick<
  PurchaseOrder,
  | 'type'
  | 'vendor'
  | 'sourceHub'
  | 'supplierName'
  | 'transferOutboundOrder'
  | 'hubTransfer'
>;

/** Table cell: vendor name, "Hub asal: X" (+ outbound link) or "Diterima dari: X". */
export const PoSourceCell: React.FC<{ po: PoSourceFields }> = ({ po }) => {
  const type = poTypeOf(po);
  const src = poSourceOf(po);
  if (type === 'REGULAR') {
    return (
      <div>
        <p className={nameCls}>{src.value}</p>
        {src.sub && <p className={`${metaCls} font-mono`}>{src.sub}</p>}
      </div>
    );
  }
  return (
    <div>
      <p className={nameCls}>{`${src.label}: ${src.value}`}</p>
      {type === 'TRANSFER' && po.transferOutboundOrder && (
        <Link
          to={`${WAREHOUSE_PATHS.outboundRequest}/${po.transferOutboundOrder.id}`}
          onClick={(e) => e.stopPropagation()}
          className={linkCls}
        >
          {po.transferOutboundOrder.code}
        </Link>
      )}
    </div>
  );
};

/** Detail "Info" rows: type, source by type, and the transfer outbound link. */
export const PoSourceKVs: React.FC<{ po: PoSourceFields }> = ({ po }) => {
  const type = poTypeOf(po);
  const src = poSourceOf(po);
  return (
    <>
      <KV label="Tipe PO" value={<PoTypeTag type={type} />} />
      <KV label={src.label} value={src.value} sub={src.sub ?? undefined} />
      {type === 'TRANSFER' && po.hubTransfer && (
        <KV
          label="Transfer warehouse"
          value={
            <Link
              to={`${WAREHOUSE_PATHS.hubTransfer}/${po.hubTransfer.id}`}
              className={linkCls}
              data-testid="po-hub-transfer-link"
            >
              {po.hubTransfer.code}
            </Link>
          }
        />
      )}
      {type === 'TRANSFER' && (
        <KV
          label="Outbound transfer"
          value={
            po.transferOutboundOrder ? (
              <Link
                to={`${WAREHOUSE_PATHS.outboundRequest}/${po.transferOutboundOrder.id}`}
                className={linkCls}
              >
                {po.transferOutboundOrder.code}
              </Link>
            ) : null
          }
        />
      )}
    </>
  );
};

/**
 * Where "Batalkan" would be on a TRANSFER PO that could still be cancelled:
 * its goods already left the source hub, so it goes with its outbound order
 * ("Batal submit shipping", while the goods are still at the hub).
 */
export const TransferCancelHint: React.FC<{
  po: Pick<
    PurchaseOrder,
    'type' | 'status' | 'totalReceived' | 'transferOutboundOrder'
  >;
}> = ({ po }) => {
  if (
    poTypeOf(po) !== 'TRANSFER' ||
    po.status !== 'CONFIRMED' ||
    po.totalReceived !== 0
  )
    return null;
  const order = po.transferOutboundOrder;
  return (
    <Alert
      status="information"
      appearance="stroke"
      size="lg"
      title="PO transfer dibatalkan lewat outbound order asalnya"
      data-testid="po-transfer-cancel-hint"
      action={
        order ? (
          <Link
            to={`${WAREHOUSE_PATHS.outboundRequest}/${order.id}`}
            className={linkCls}
            data-testid="po-transfer-cancel-hint-link"
          >
            {`Buka ${order.code}`}
          </Link>
        ) : undefined
      }
    >
      Barangnya sudah keluar dari hub asal. Selama masih di hub, pakai Batal
      submit shipping di outbound order-nya.
    </Alert>
  );
};

/** "Transfer → Hub X" marker for outbound rows / headers (nothing for REGULAR). */
export const OutboundTransferTag: React.FC<{
  order: Pick<OutboundOrder, 'type' | 'destinationHub'>;
}> = ({ order }) => {
  if (order.type !== 'TRANSFER') return null;
  const dest = order.destinationHub?.name;
  return (
    <Tag
      size="xs"
      variant="stroke"
      icon={
        <i className={cn(OUTBOUND_TYPE_META.TRANSFER.icon, 'text-[11px]')} />
      }
      data-testid="oo-transfer-tag"
    >
      {dest ? `Transfer → ${dest}` : 'Transfer'}
    </Tag>
  );
};
